///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2021
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { createSlice } from "@reduxjs/toolkit";
import _findIndex from "lodash/findIndex";
import _filter from "lodash/filter";
import { isCarrier } from "../helpers/goTrackUserUtils";
import types from "../helpers/types";
import { getSpotLegsWithFilters, getSpotLegsWithSearchTerm, getSpotLegsWithMultiFilters } from "../api/api";
import { fetchSpotLegById } from "./spotLegSlice";

export const initialState = {
  isFetching: false,
  spotLegs: [],
  totalCount: 0,
};

const spotLegsSlice = createSlice({
  name: "spotLegsSlice",
  initialState,
  reducers: {
    requestSpotLegs: (state) => {
      state.isFetching = true;
    },
    receiveSpotLegsSuccess: (state, { payload }) => {
      state.spotLegs = payload.results;
      state.totalCount = payload.pagingParams.totalRecords;
      state.isFetching = false;
    },
    receiveSpotLegsFailure: (state) => {
      state.isFetching = false;
    },
    setSpotLegUpdates: (state, { payload }) => {
      const indexOfLeg = _findIndex(state.spotLegs, {
        spotLegsId: payload.spotLegId,
      });
      const updatedLeg = { ...state.spotLegs[indexOfLeg], ...payload.updates };
      state.spotLegs[indexOfLeg] = updatedLeg;
    },
    removeSpotLeg: (state, { payload }) => {
      const updatedSpotLegs = _filter(
        state.spotLegs,
        (spotLeg) => spotLeg.spotLegsId !== payload
      );
      state.spotLegs = updatedSpotLegs;
    },
  },
});

const GOTRACK_LIMIT = window.env.GOTRACK_LIMIT;

export const {
  requestSpotLegs,
  receiveSpotLegsSuccess,
  receiveSpotLegsFailure,
  setSpotLegUpdates,
  removeSpotLeg,
} = spotLegsSlice.actions;

export const fetchSpotLegsWithFilters =
  (page) => async (dispatch, getState) => {
    let spotLegs;
    const state = getState();
    const { currentPage, searchTerm, status, ...spotFilters } =
      state.spotFilters;
    dispatch(requestSpotLegs());
    try {
      let statusMap;
      if (!isCarrier(state.goTrackUser.goTrackUser.roles)) {
        statusMap = types.spotLegStatusWithCreationType.find(
          (obj) => obj.id === parseInt(status, 10)
        );
      } else {
        statusMap = types.carrierSpotLegStatusWithCreationType.find(
          (obj) => obj.id === parseInt(status, 10)
        );
      } // status comes in as an id for types.spotLegStatusWithCreationType

      const params = {
        limit: GOTRACK_LIMIT,
        offset: page * GOTRACK_LIMIT,
        status: statusMap.status,
        ...spotFilters,
      };

      if (statusMap.creationType) {
        params.creationType = statusMap.creationType;
      }

      spotLegs = await getSpotLegsWithFilters(params);
    } catch (e) {
      dispatch(receiveSpotLegsFailure());
      return;
    }
    dispatch(receiveSpotLegsSuccess(spotLegs));
  };

export const fetchSpotLegsWithSearchTerm =
  (page) => async (dispatch, getState) => {
    let spotLegs;
    const { searchTerm, sortType, sortDirection, spotLegId } =
      getState().spotFilters;

    dispatch(requestSpotLegs());
    try {
      const params = {
        limit: GOTRACK_LIMIT,
        offset: page * GOTRACK_LIMIT,
        searchTerm,
        sortType,
        sortDirection,
      };
      spotLegs = await getSpotLegsWithSearchTerm(params);
    } catch {
      dispatch(receiveSpotLegsFailure());
      return;
    }
    dispatch(receiveSpotLegsSuccess(spotLegs));

    if (spotLegId) {
      dispatch(fetchSpotLegById(spotLegId));
    }
  };

export const fetchSpotLegsV2 =
  (updatedSpotLegFilters) => async (dispatch, getState) => {
    let spotLegs;
    const state = getState();
    const { status } = updatedSpotLegFilters;
    dispatch(requestSpotLegs());

    try {
      let statusMap;

      if (!isCarrier(state.goTrackUser.goTrackUser.roles)) {
        statusMap = types.spotLegStatusWithCreationType.find(
          (obj) => obj.id === parseInt(status, 10)
        );
      } else {
        statusMap = types.carrierSpotLegStatusWithCreationType.find(
          (obj) => obj.id === parseInt(status, 10)
        );
      } // status comes in as an id for types.spotLegStatusWithCreationType

      const params = {
        ...updatedSpotLegFilters,
        limit: GOTRACK_LIMIT,
        offset: updatedSpotLegFilters.currentPage * GOTRACK_LIMIT,
        status: statusMap.status,
      };

      if (statusMap.creationType) {
        params.creationType = statusMap.creationType;
      }

      spotLegs = await getSpotLegsWithMultiFilters(params, '2.0');
    }
    catch (e) {
      dispatch(receiveSpotLegsFailure());
      return;
    }
    dispatch(receiveSpotLegsSuccess(spotLegs));
  };

export const spotLegsReducer = spotLegsSlice.reducer;
